import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Play, RotateCw, Square } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { usePoll } from "../../hooks/usePoll";
import { useModels, ModelSelect } from "../book/shared";

const TONE = { done: "text-emerald-300", running: "text-amber-300", skipped: "text-slate-500", stopped: "text-rose-300", pending: "text-slate-400" };

const StartForm = ({ sid, onStarted }) => {
  const models = useModels();
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/series/${sid}/polish-presets`).then((r) => setPresets(r.data)).catch(() => {}); }, [sid]);
  useEffect(() => { if (models && !model) setModel(models.default); }, [models, model]);
  const start = async () => {
    setBusy(true);
    try { onStarted((await api.post(`/series/${sid}/polish`, preset ? { preset_id: preset } : { model })).data); toast.success("Series polish started"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={`series-polish-form-${sid}`}>
      <p className="text-xs text-slate-400">Runs one-click polish on every book in book-number order, one after another. Each book still stops for you to check its findings.</p>
      {presets.length > 0 && (
        <select value={preset} onChange={(e) => setPreset(e.target.value)} className="h-8 w-full rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid={`series-polish-preset-${sid}`}>
          <option value="">No preset — choose a model</option>
          {presets.map((p) => <option key={p.id} value={p.id}>Preset: {p.name}</option>)}
        </select>
      )}
      {!preset && <ModelSelect value={model} onChange={setModel} models={models} testId={`series-polish-model-${sid}`} />}
      <button type="button" disabled={busy} onClick={start} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-60" data-testid={`series-polish-start-${sid}`}><Play size={12} />Polish the whole series</button>
    </div>
  );
};

export const SeriesPolishPanel = ({ sid, currentBookId }) => {
  const [sp, setSp] = useState(undefined);
  const [fresh, setFresh] = useState(false);
  const load = useCallback(() => api.get(`/series/${sid}/polish`).then((r) => setSp(r.data)).catch(() => setSp(null)), [sid]);
  useEffect(() => { load(); }, [load]);
  const active = sp && ["running", "waiting"].includes(sp.status);
  usePoll(load, active, 5000);
  const act = async (a) => { try { await api.post(`/series-polish/${sp.id}/${a}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (sp === undefined) return null;
  return (
    <div className="mt-4 rounded-lg border border-slate-800 p-4 space-y-3" data-testid={`series-polish-${sid}`}>
      <p className="eyebrow inline-flex items-center gap-1.5"><Layers size={12} />Series polish</p>
      {!sp || fresh ? <StartForm sid={sid} onStarted={(x) => { setFresh(false); setSp(x); }} /> : (
        <>
          <p className="text-sm text-slate-300" data-testid={`series-polish-message-${sid}`}>{sp.message}{active && sp.current?.message ? ` ${sp.current.message}` : ""}</p>
          <ol className="space-y-1">
            {sp.books.map((b, i) => (
              <li key={b.book_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`series-polish-book-${i}`}>
                <span className="min-w-0 truncate"><span className="font-mono text-amber-500/80 mr-2">#{b.book_number ?? "?"}</span>{b.title}</span>
                <span className={`shrink-0 font-mono uppercase tracking-wider ${TONE[b.status]}`}>{b.status}{b.note ? ` · ${b.note}` : ""}</span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-2">
            {sp.current_pipeline_id && active && sp.books[sp.idx]?.book_id !== currentBookId && <Link to={`/app/books/${sp.books[sp.idx]?.book_id}?tab=pipeline`} className="h-8 px-3 leading-8 rounded-md text-xs bg-emerald-700 hover:bg-emerald-600 text-white" data-testid={`series-polish-open-${sid}`}>{sp.current?.status === "waiting" ? "Your check is needed — open book →" : "Open current book →"}</Link>}
            {sp.status === "waiting" && <button type="button" onClick={() => act("retry")} className="h-8 px-3 rounded-md text-xs bg-amber-600 text-white inline-flex items-center gap-1" data-testid={`series-polish-retry-${sid}`}><RotateCw size={12} />Retry</button>}
            {active && <button type="button" onClick={() => window.confirm("Stop the series polish? Books already polished keep their new versions.") && act("cancel")} className="h-8 px-3 rounded-md text-xs border border-slate-700 text-slate-300 inline-flex items-center gap-1" data-testid={`series-polish-cancel-${sid}`}><Square size={12} />Stop</button>}
            {!active && <button type="button" onClick={() => setFresh(true)} className="h-8 px-3 rounded-md text-xs border border-slate-700 text-slate-300" data-testid={`series-polish-again-${sid}`}>Run again</button>}
          </div>
        </>
      )}
    </div>
  );
};
